"use client";

import { Fragment } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MDECBadge } from "@/components/ui/mdec-badge";
import { HRDCorpBadge } from "@/components/ui/hrd-corp-badge";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { OpenAIPartnerChip } from "@/components/ui/openai-partner-badge";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

const CAPABILITY_CHIPS = [
  "AI Agents",
  "RAG Systems",
  "Automation",
];

// Splits a translated message on the [[...]] accent marker and wraps the
// marked phrase in the emphasis underline — translators can reorder freely.
function renderAccent(text: string) {
  return text.split(/\[\[(.+?)\]\]/g).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

interface HeroSectionProps {
  locale?: Locale;
}

export default function HeroSection({
  locale = defaultLocale,
}: HeroSectionProps) {
  const prefersReducedMotion = useReducedMotion();

  // Hero entrance moves only: opacity stays 1 so the headline and intro are
  // visible in the static HTML and paint before JS hydrates (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  return (
    <section className="relative bg-background">
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-28 lg:pt-24">
        <div className="max-w-3xl">
          <div>
            <motion.p
              {...reveal(0)}
              className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
            >
              {t(locale, "Agentic AI Studio · Malaysia & Singapore")}
            </motion.p>

            <motion.h1
              {...reveal(0.08)}
              className="mb-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
            >
              {renderAccent(
                t(locale, "We build [[AI agents]] that get real work done"),
              )}
            </motion.h1>

            <motion.p
              {...reveal(0.16)}
              className="mb-8 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
            >
              {t(
                locale,
                "Custom AI agents on Claude that run your real workflows — designed, built, evaluated and run for you, with a human in the loop.",
              )}
            </motion.p>

            <motion.div
              {...reveal(0.24)}
              className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <Button
                asChild
                className="w-full rounded-full bg-primary px-8 py-3 text-base font-semibold text-primary-foreground transition hover:bg-primary-emphasis sm:w-auto"
              >
                <Link
                  href={getLocalizedPathname(
                    "/solutions/agent-use-cases/",
                    locale,
                  )}
                  data-track-event="cta_click"
                  data-track-location="hero_explore_use_cases"
                >
                  {t(locale, "Explore use cases")}
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
                </Link>
              </Button>
              <Link
                href={getLocalizedPathname("/contact/", locale)}
                className="group inline-flex w-full items-center justify-center gap-2 text-base font-semibold text-neutral-emphasis transition hover:text-primary sm:w-auto"
              >
                {t(locale, "Talk to us")}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden
                />
              </Link>
            </motion.div>

            {/* Capability index */}
            <motion.ul
              {...reveal(0.32)}
              className="mb-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-border pt-6"
              aria-label={t(locale, "Core AI capabilities")}
            >
              {CAPABILITY_CHIPS.map((chip, index) => (
                <li
                  key={chip}
                  className="flex items-center gap-2.5 text-sm font-medium text-neutral-muted"
                >
                  <span className="font-mono text-xs text-primary">
                    0{index + 1}
                  </span>
                  {t(locale, chip)}
                </li>
              ))}
            </motion.ul>

            <motion.div {...reveal(0.4)} className="flex flex-wrap gap-3">
              <AnthropicPartnerBadge variant="hero" />
              <OpenAIPartnerChip
                href={getLocalizedPathname("/partners/openai-partner/", locale)}
              />
              <MDECBadge variant="hero" />
              <HRDCorpBadge variant="hero" />
            </motion.div>
          </div>

        </div>
      </div>
      <div className="h-px w-full bg-border" aria-hidden />
    </section>
  );
}
